// InternalRequestDashboard.tsx
import React, {useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";
import {useNavigate} from "react-router-dom";
import NavBar from "../NavBar/NavBar";
import {getIdMeBaseApiUrl} from "../../../utils/UrlConstants";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./RequestDashboard.scss";
import Dropdown from "../../../utils/components/Dropdown";
import MultiSelectDropdown from "../../../utils/components/MultiSelectDropdown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../common/Button";
import FooterData from "../NavBar/FooterData";
import {useMsal} from '@azure/msal-react';
import InternalRoleRequests from "./InternalRoleRequests";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../../utils/authUtils";
import useId from "@mui/material/utils/useId";
import { useIdentityAppService } from "../../../services/IdentityServices";

// Types for API responses
interface ApplicationDto {
    appCd: string;
    applicationName: string;
    applicationDesc: string;
    orgCd: string;
    orgName: string;
    appOwner: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    allowExternalAccess: string;
    allowInternalAccess: string;
}

interface EnvironmentDto {
    appEnvCd: string;
    envName: string;
    envDesc: string;
    appCd: string;
    applicationName: string;
    idpCd: string;
    idpName: string;
    ssoType: string;
    appIdpIdentifier: string;
    uri: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
}

interface RoleDto {
    roleCd: string;
    roleName: string;
    roleDesc: string;
    appCd: string;
    applicationName: string;
    appEnvCd: string;
    envName: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    roleIdentifier: string;
}

interface ApplicationListResponse {
    success: boolean;
    message: string;
    applications: ApplicationDto[];
    count: number;
    timestamp: string;
}

interface EnvironmentListResponse {
    success: boolean;
    message: string;
    environments: EnvironmentDto[];
    count: number;
    timestamp: string;
}

interface RoleListResponse {
    success: boolean;
    message: string;
    roles: RoleDto[];
    count: number;
    timestamp: string;
}

// Add User Search Response interface
interface UserSearchResponse {
    success: boolean;
    message: string;
    users: any[];
    count: number;
    timestamp: string;
}

interface MultiSelectChangeEvent {
    target: {
        name: string;
        value: (string | number)[];
    };
}

interface FormErrors {
    [key: string]: string;
}

const InternalRequestDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { accounts } = useMsal();

    const [userName, setUserName] = useState<string>();
    const [userId, setUserId] = useState<string>();
    const [selectedApplication, setSelectedApplication] = useState<string>('');
    const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
    const [selectedRoles, setSelectedRoles] = useState<(string | number)[]>([]);

    // Data state using new structure
    const [applications, setApplications] = useState<ApplicationDto[]>([]);
    const [environments, setEnvironments] = useState<EnvironmentDto[]>([]);
    const [roles, setRoles] = useState<RoleDto[]>([]);

    // Loading state
    const [loadingApplications, setLoadingApplications] = useState(false);
    const [loadingEnvironments, setLoadingEnvironments] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);

    // Error state
    const [errors, setErrors] = useState<FormErrors>({});
    const [error, setError] = useState<string | null>(null);

    const [notes, setNotes] = useState<string>('');
    const applicationRefNotes = useRef(null);
    const [refreshRequestHistory, setRefreshRequestHistory] = useState(false);

    const [currentUser, setCurrentUser] = useState<any>(null);

    // Helper function to get current user data from sessionStorage
    const getCurrentUserFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            return storedUserData ? JSON.parse(storedUserData) : null;
        } catch (error) {
            console.error("Error parsing current user data from session:", error);
            return null;
        }
    };

    // Helper function to get current user ID from sessionStorage
    const getCurrentUserIdFromSession = () => {
        const userData = getCurrentUserFromSession();
        return userData?.userId || null;
    };

    // Add new state for user search data
    const [sessionUserData, setSessionUserData] = useState<any>(null);

    // Function to load user data from session storage
    const loadUserDataFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            if (storedUserData) {
                const userData = JSON.parse(storedUserData);
                setSessionUserData(userData);
                console.log("Loaded user data from session:", userData);
            }
        } catch (error) {
            console.error("Error loading user data from session:", error);
        }
    };

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }
        
        // Get user info from MSAL
        if (accounts && accounts.length > 0) {
            const account = accounts[0];
            setUserName(account.username);
            setUserId(account.localAccountId);
        }

        // Load user data from session storage first
        loadUserDataFromSession();
    }, [accounts]);

    // Reset environment and roles when application changes
    useEffect(() => {
        if (selectedApplication) {
            loadEnvironments(selectedApplication);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        } else {
            setEnvironments([]);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        }
    }, [selectedApplication]);

    // Reset roles when environment changes
    useEffect(() => {
        if (selectedApplication && selectedEnvironment) {
            // For internal users, filter by Internal IDP
            loadRoles(selectedApplication, selectedEnvironment, 'Internal');
            setSelectedRoles([]);
        } else {
            setRoles([]);
            setSelectedRoles([]);
        }
    }, [selectedApplication, selectedEnvironment]);

    // Load environments for selected application
    const loadEnvironments = async (appCd: string) => {
        try {
            setLoadingEnvironments(true);

            const response = await useIdentityAppService().getApplicationEnvironmentsByAppCd(appCd);

            if (response.ok) {
                const data: EnvironmentListResponse = await response.json();
                setEnvironments(data.environments);
                console.log(`Loaded ${data.environments.length} environments for application ${appCd}`);
            } else {
                const errorData = await response.json();
                console.error('Failed to load environments:', errorData);
                setEnvironments([]);
            }
        } catch (err) {
            console.error('Error loading environments:', err);
            setEnvironments([]);
        } finally {
            setLoadingEnvironments(false);
        }
    };

    // Load roles for selected application and environment, optionally filtered by IDP name
    const loadRoles = async (appCd: string, envCd: string, idpName?: string) => {
        try {
            setLoadingRoles(true);

            // Build URL with optional idpName query parameter
            let url = `${getIdMeBaseApiUrl()}/application/${appCd}/environments/${envCd}/roles`;
            if (idpName && idpName.trim() !== '') {
                url += `?idpName=${encodeURIComponent(idpName)}`;
            }

            const response = await useIdentityAppService().getApplicationEnvironmentsByIdpName(url);

            if (response.ok) {
                const data: RoleListResponse = await response.json();
                setRoles(data.roles);

                const logMessage = idpName
                    ? `Loaded ${data.roles.length} roles for application ${appCd}, environment ${envCd}, and IDP ${idpName}`
                    : `Loaded ${data.roles.length} roles for application ${appCd} and environment ${envCd}`;
                console.log(logMessage);
            } else {
                const errorData = await response.json();
                console.error('Failed to load roles:', errorData);
                setRoles([]);
            }
        } catch (err) {
            console.error('Error loading roles:', err);
            setRoles([]);
        } finally {
            setLoadingRoles(false);
        }
    };

    // Handle application dropdown change
    const handleApplicationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const appCd = event.target.value;
        setSelectedApplication(appCd);

        // Clear errors when user makes selections
        if (errors.application) {
            setErrors(prev => ({...prev, application: ''}));
        }
    };

    // Handle environment dropdown change
    const handleEnvironmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const envCd = event.target.value;
        setSelectedEnvironment(envCd);

        // Clear errors when user makes selections
        if (errors.environment) {
            setErrors(prev => ({...prev, environment: ''}));
        }
    };

    // Handle role multi-select change
    const handleRoleChange = (event: MultiSelectChangeEvent) => {
        const roleIds = event.target.value;
        setSelectedRoles(roleIds);

        // Clear errors when user makes selections
        if (errors.roles) {
            setErrors(prev => ({...prev, roles: ''}));
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!selectedApplication) {
            newErrors.application = 'Please select an application';
        }

        if (!selectedEnvironment) {
            newErrors.environment = 'Please select an environment';
        }

        if (selectedRoles.length === 0) {
            newErrors.roles = 'Please select at least one role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            let userResult = null;
            let currentUserToUse = null;

            // Check if we have session user data
            if (sessionUserData) {
                // Use existing session user data
                console.log("Using existing session user data:", sessionUserData);
                userResult = sessionUserData;

                currentUserToUse = {
                    userId: sessionUserData.userId || userId,
                    userLogin: sessionUserData.userName || userName,
                    userType: sessionUserData.userType || "Internal",
                    firstName: sessionUserData.firstName || accounts[0]?.name?.split(' ')[0] || "",
                    lastName: sessionUserData.lastName || accounts[0]?.name?.split(' ').slice(1).join(' ') || "",
                    userEmail: sessionUserData.email || accounts[0]?.username || "",
                    userPhone: sessionUserData.phone || "",
                    notes: sessionUserData.notes || ""
                };
            } else {
                // No session data exists, call check-or-create-user API
                console.log("No session user data found, calling check-or-create-user API");

                const userRequest = {
                    userType: "Internal",
                    userName: userName,
                    firstName: accounts[0]?.name?.split(' ')[0] || "",
                    lastName: accounts[0]?.name?.split(' ').slice(1).join(' ') || "",
                    email: accounts[0]?.username || "",
                    phone: "",
                    notes: "",
                    userId: userId,
                    organizationName: "",
                    organizationType: "",
                    stateIssuedId: "",
                    organizationNotes: ""
                };

                console.log("Checking or creating user:", userRequest);

                const userResponse = await useIdentityAppService().postUserCheckOrCreate(JSON.stringify(userRequest));

                if (!userResponse.ok) {
                    const errorData = await userResponse.json();
                    console.error("Failed to check or create user:", errorData);
                    toast.error("Failed to verify user information. Please try again.");
                    return;
                }

                userResult = await userResponse.json();
                console.log("User check/create result:", userResult);

                // Store the new user data in session for future use
                setSessionUserData(userResult);
                sessionStorage.setItem('currentUserData', JSON.stringify(userResult));

                // Create user object to match AssignRoles format
                currentUserToUse = {
                    userId: userResult.userId || userId,
                    userLogin: userRequest.userName,
                    userType: userRequest.userType,
                    firstName: userRequest.firstName,
                    lastName: userRequest.lastName,
                    userEmail: userRequest.email,
                    userPhone: userRequest.phone,
                    notes: userRequest.notes
                };
            }

            setCurrentUser(currentUserToUse);

            // Step 3: Prepare assignment data exactly like AssignRoles
            const assignmentData = {
                userIds: [currentUserToUse.userId],
                applicationCode: selectedApplication,
                environmentCode: selectedEnvironment,
                roleIds: selectedRoles,
                userType: currentUserToUse.userType,
                assignedTo: currentUserToUse.userLogin, // For internal users, they assign to themselves
                requestedBy: accounts[0]?.localAccountId || userId, // Add requestedBy for auto-approval logic
                notes: notes.trim() || null,
                startDate: new Date().toISOString().split('T')[0], // Current date
                endDate: (() => {
                    const today = new Date();
                    const endDate = new Date(today);
                    endDate.setDate(today.getDate() + 364); // Add 364 days
                    return endDate.toISOString().split('T')[0];
                })()
            };

            console.log("Submitting role assignment:", assignmentData);

            // Call the same API endpoint as AssignRoles
            const response = await useIdentityAppService().postUserAssignRoles(JSON.stringify(assignmentData));

            if (response.ok) {
                const data = await response.json();
                console.log('Role assignment response:', data);

                if (data.success) {
                    toast.success("Role assignment request submitted successfully!");
                    setRefreshRequestHistory((prev) => !prev);

                    // Reset form
                    setSelectedApplication('');
                    setSelectedEnvironment('');
                    setSelectedRoles([]);
                    setNotes('');
                } else {
                    toast.error(data.message || 'Role assignment request failed');

                    // Show individual errors if available
                    if (data.errors && data.errors.length > 0) {
                        data.errors.forEach((error: string) => {
                            toast.error(error, {autoClose: 6000});
                        });
                    }
                }
            } else {
                // Handle HTTP errors (non-200 responses including 500)
                let errorData: any;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    errorData = {
                        error: 'Unknown error occurred',
                        message: `HTTP ${response.status}: ${response.statusText}`
                    };
                }

                console.error('Role assignment failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData: errorData
                });

                // Show main error toast
                toast.error('Role assignment failed.', { autoClose: 7000 });

                // If the response contains detailed error information, display it
                if (errorData.errors && errorData.errors.length > 0) {
                    errorData.errors.forEach((err: string) => {
                        console.error('Role assignment error:', err);
                        toast.error(err, { autoClose: 6000 });
                    });
                }

                // Show failed assignment details if available
                if (errorData.assignmentDetails && errorData.assignmentDetails.length > 0) {
                    const failedAssignments = errorData.assignmentDetails.filter(
                        (detail: any) => detail.status === 'FAILED'
                    );

                    if (failedAssignments.length > 0) {
                        failedAssignments.forEach((assignment: any) => {
                            console.error('Failed assignment:', assignment);
                            toast.error(
                                `Failed to assign ${assignment.roleName} to ${assignment.userName}: ${assignment.message}`,
                                { autoClose: 8000 }
                            );
                        });
                    }
                }
            }

        } catch (error) {
            console.error("Error submitting role assignment request:", error);
            toast.error("Role assignment failed.");
        }
    };

    function joinClasses(...classes: (string | undefined)[]): string {
        return classes.filter(Boolean).join(" ");
    }

    const wrapperClasses = joinClasses("usa-fieldset usa-form-group");

    const handleRichTextChangeAppNotes = (event: any) => {
        setNotes(event);
        if (applicationRefNotes.current != null) {
            const appDetails = event;
        }
    };

    // Convert data to dropdown choices
    const applicationChoices = applications.map(app => ({
        value: app.appCd,
        label: app.applicationDesc ? `${app.applicationName} - ${app.applicationDesc}` : app.applicationName
    }));

    const environmentChoices = environments.map(env => ({
        value: env.appEnvCd,
        label: env.envName
    }));

    const roleChoices = roles.map(role => ({
        value: role.roleCd,
        label: role.roleName
    }));

    useEffect(() => {
        loadNonAdminApplications();
    }, [accounts]);

// Replace the existing loadApplications method with this new method
    const loadNonAdminApplications = async () => {
        if (accounts.length === 0) {
            return;
        }

        // Get OID from MSAL
        const userIdpGuid = accounts[0].idTokenClaims?.oid;
        if (!userIdpGuid) {
            setError('User GUID not found');
            return;
        }

        try {
            setLoadingApplications(true);
            setError(null);

            console.log('Loading applications that allow internal access');

            // Call the internal access applications endpoint
            const response = await useIdentityAppService().getApplicationEnvironmentsByUserIdpGuid(userIdpGuid);

            if (response.ok) {
                const data: ApplicationListResponse = await response.json();

                if (data.success) {
                    setApplications(data.applications);
                    console.log(`Loaded ${data.applications.length} applications that allow internal access`);

                    // Log which applications are available for internal access
                    if (data.applications.length > 0) {
                        const appNames = data.applications.map(app => app.applicationName);
                        console.log('Applications allowing internal access:', appNames);
                    } else {
                        console.log('No applications allow internal access');
                    }
                } else {
                    setError(data.message || 'Failed to load applications');
                    setApplications([]);
                }
            } else if (response.status === 400) {
                const errorData = await response.json();
                setError(errorData.message || 'Invalid request');
                setApplications([]);
            } else if (response.status === 404) {
                setError('No applications found that allow internal access');
                setApplications([]);
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.message || 'Failed to load applications');
                setApplications([]);
            }
        } catch (err) {
            console.error('Error loading applications for internal access:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading applications');
            setApplications([]);
        } finally {
            setLoadingApplications(false);
        }
    };

    return (
        <>
            <Box>
                <NavBar/>
<ToastContainer position="top-right" autoClose={false} closeButton closeOnClick={false} hideProgressBar />
                <Box sx={{padding: 4}}>
                    <div className={"subheading grid-row"}>
                        <div className={"grid-col flex-2"}>
                            <h2 className={"subheading-title"}>Internal Request Dashboard</h2>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loadingApplications && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            fontSize: '16px',
                            color: '#666'
                        }}>
                            <div>Loading available applications...</div>
                        </div>
                    )}

                    {/* No Applications Available Message */}
                    {!loadingApplications && applications.length === 0 && !error && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#ff9800',
                            fontSize: '18px',
                            backgroundColor: '#fff3e0',
                            borderRadius: '8px',
                            border: '1px solid #ffb74d',
                            margin: '20px 0'
                        }}>
                            <h4 style={{ marginBottom: '16px', color: '#ff9800' }}>No Applications Available for Internal Access Request</h4>
                            <p>No applications are currently configured to allow internal access requests.</p>
                            <p style={{ marginTop: '16px', fontSize: '16px' }}>
                                Only applications marked as "Allow Internal Access" appear in this list.
                            </p>
                            <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                                Contact your system administrator to enable internal access for applications.
                            </p>
                            <Button
                                onClick={() => navigate("/admin-dashboard")}
                            >
                                Go to Admin Dashboard
                            </Button>
                        </div>
                    )}

                    {/* Error Message */}
                    {!loadingApplications && error && (
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            color: '#c62828'
                        }}>
                            <strong>Error:</strong> {error}
                            <div style={{ marginTop: '10px' }}>
                                <Button
                                    onClick={() => loadNonAdminApplications()}
                                >
                                    Retry
                                </Button>
                                <Button
                                    onClick={() => navigate("/admin-dashboard")}
                                >
                                    Return to Dashboard
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Normal Form - Only show if user has non-admin applications */}
                    {!loadingApplications && applications.length > 0 && !error && (
                        <>
                            <div className={"form-layout"}>
                                <Box sx={{padding: 4}}>
                                    <div className={"row"}>
                                        <div className={"element"}>
                                            <Dropdown
                                                choices={applicationChoices}
                                                emptyChoiceLabel={loadingApplications ? "Loading applications..." : "Select Application Name"}
                                                label={`Application Name`}
                                                name={"appName"}
                                                value={selectedApplication}
                                                onChange={handleApplicationChange}
                                                mandatoryLabel={true}
                                                disabled={loadingApplications}
                                            />
                                            {errors.application && (
                                                <div style={{color: "red", marginTop: "5px"}}>
                                                    {errors.application}
                                                </div>
                                            )}
                                            <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                                                Showing applications that allow internal access requests
                                            </div>
                                        </div>
                                        <div className={"element"}>
                                            <MultiSelectDropdown
                                                choices={roleChoices}
                                                emptyChoiceLabel={
                                                    !selectedEnvironment
                                                        ? "Select environment first"
                                                        : loadingRoles
                                                            ? "Loading roles..."
                                                            : roles.length === 0
                                                                ? "No roles available"
                                                                : "Select Roles"
                                                }
                                                label={"Roles"}
                                                name={"roles"}
                                                value={selectedRoles}
                                                onChange={handleRoleChange}
                                                disabled={!selectedEnvironment || loadingRoles}
                                                mandatoryLabel={true}
                                                searchable={true}
                                                showSelectedAsTags={true}
                                                allowSelectAll={true}
                                            />
                                            {errors.roles && (
                                                <div style={{color: "red", marginTop: "5px"}}>
                                                    {errors.roles}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={"row"}>
                                        <div className={"element"}>
                                            <Dropdown
                                                choices={environmentChoices}
                                                emptyChoiceLabel={
                                                    !selectedApplication
                                                        ? "Select application first"
                                                        : loadingEnvironments
                                                            ? "Loading environments..."
                                                            : environments.length === 0
                                                                ? "No environments available"
                                                                : "Select Environment"
                                                }
                                                label={"Environment"}
                                                name={"environment"}
                                                value={selectedEnvironment}
                                                disabled={!selectedApplication || loadingEnvironments}
                                                onChange={handleEnvironmentChange}
                                                mandatoryLabel={true}
                                            />
                                            {errors.environment && (
                                                <div style={{color: "red", marginTop: "5px"}}>
                                                    {errors.environment}
                                                </div>
                                            )}
                                        </div>
                                        <div className="element">
                                            <fieldset className={wrapperClasses}>
                                        <span>
                                            <label htmlFor="my-editor">
                                                <b>Request Notes:</b>
                                            </label>
                                            <ReactQuill
                                                className="my-editor"
                                                style={{maxHeight: "100px", overflowY: "auto"}}
                                                value={notes}
                                                ref={applicationRefNotes}
                                                onChange={handleRichTextChangeAppNotes}
                                                modules={{
                                                    toolbar: [
                                                        ["bold", "italic", "underline", "strike"],
                                                        [{list: "ordered"}, {list: "bullet"}],
                                                        [{align: []}],
                                                        ["clean"],
                                                    ],
                                                }}
                                            />
                                        </span>
                                            </fieldset>
                                        </div>
                                    </div>
                                </Box>
                            </div>

                            <div className={"row"}>
                                <div className={"element"}></div>
                                <div className={"element"} style={{display: "flex", justifyContent: "flex-end"}}>
                                    <Button
                                        onClick={() => navigate("/internal-home")}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        className={"primary-button"}
                                        disabled={!selectedApplication || !selectedEnvironment || selectedRoles.length === 0}
                                    >
                                        Submit Request
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    <InternalRoleRequests refreshTrigger={refreshRequestHistory} userId={getCurrentUserIdFromSession()}/>
                </Box>
                <FooterData
                    styles={true}
                />
            </Box>
        </>
    );
};

export default InternalRequestDashboard;
